/**
 * Card component for displaying a database connection
 */

import { Database, MoreVertical, Zap, ScanSearch } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConnectionStatusBadge } from '@/components/discovery/connection-status-badge'
import { formatRelativeTime, dbTypeLabels } from '@/lib/discovery-utils'
import type { DataConnection } from '@/types/discovery'

interface ConnectionCardProps {
  readonly connection: DataConnection
  readonly onTest: (connection: DataConnection) => void
  readonly onScan: (connection: DataConnection) => void
  readonly onClick: (connection: DataConnection) => void
  readonly onEdit: (connection: DataConnection) => void
  readonly onDelete: (connection: DataConnection) => void
  readonly isTestPending?: boolean
}

export function ConnectionCard({
  connection,
  onTest,
  onScan,
  onClick,
  onEdit,
  onDelete,
  isTestPending,
}: ConnectionCardProps) {
  return (
    <Card
      className="cursor-pointer transition-colors hover:bg-accent/50"
      onClick={() => onClick(connection)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">{connection.name}</CardTitle>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit(connection)
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(connection)
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardDescription>
          {dbTypeLabels[connection.databaseType] ?? connection.databaseType}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <ConnectionStatusBadge status={connection.status} />

          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div>
              <span className="font-medium text-foreground">Last scan</span>
              <p>{formatRelativeTime(connection.lastScanAt)}</p>
            </div>
            <div>
              <span className="font-medium text-foreground">Last tested</span>
              <p>{formatRelativeTime(connection.lastTestedAt)}</p>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={isTestPending}
              onClick={(e) => {
                e.stopPropagation()
                onTest(connection)
              }}
            >
              <Zap className="mr-1 h-3 w-3" />
              Test
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={(e) => {
                e.stopPropagation()
                onScan(connection)
              }}
            >
              <ScanSearch className="mr-1 h-3 w-3" />
              Scan
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
